import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/joy";
import FullPageContainer from "pages/container/FullPageContainer";
import { theme } from "styles/theme";
import useTaskEditorStore from "hooks/useTaskEditorStore";
import {
  cloneTaskEditorState,
  createEmptyTaskEditorState,
  stableHashBlocks,
} from "./utils/taskEditorDiff";
import type { TaskEditorState } from "./utils/taskEditorUtils";
import { useTaskEditorSyncState } from "./utils/useTaskEditorSyncState";
import AgentChatPanel from "./AgentChatPanel";
import AgentDebugPanel from "./agent/AgentDebugPanel";
import { ConflictBanner } from "./agent/ConflictBanner";
import { RecoveryToast } from "./agent/RecoveryToast";
import { getSessionTask, listThreads } from "./agent/chatHistoryApi";
import TaskListPanel from "./TaskListPanel";
import TaskMetadataModal, { TaskMetadataValues } from "./TaskMetadataModal";
import TaskWorkspacePanel from "./TaskWorkspacePanel";

type MetaModalState =
  | { mode: "create" }
  | {
      mode: "edit";
      initialValues: TaskMetadataValues;
      originalTaskId: string;
    };

const TASK_LIST_WIDTH_MIN = 160;
const TASK_LIST_WIDTH_MAX = 240;
const TASK_LIST_WIDTH_DEFAULT = 160;
const TASK_LIST_WIDTH_STORAGE_KEY = "taskEditorTaskListWidth";
const RESIZE_HANDLE_WIDTH = 8;
const CHAT_MIN_WIDTH = 280;
const WORKSPACE_WIDTH_MIN = 420;
const WORKSPACE_WIDTH_MAX = 1600;
const WORKSPACE_WIDTH_DEFAULT = 480;
const WORKSPACE_WIDTH_STORAGE_KEY = "taskEditorWorkspaceWidth";

function loadInitialWorkspaceWidth(): number {
  if (typeof window === "undefined") {
    return WORKSPACE_WIDTH_DEFAULT;
  }
  try {
    const saved = window.localStorage.getItem(WORKSPACE_WIDTH_STORAGE_KEY);
    if (!saved) return WORKSPACE_WIDTH_DEFAULT;
    const parsed = parseInt(saved, 10);
    if (!Number.isFinite(parsed)) return WORKSPACE_WIDTH_DEFAULT;
    return Math.min(Math.max(parsed, WORKSPACE_WIDTH_MIN), WORKSPACE_WIDTH_MAX);
  } catch {
    return WORKSPACE_WIDTH_DEFAULT;
  }
}

function loadInitialTaskListWidth(): number {
  if (typeof window === "undefined") {
    return TASK_LIST_WIDTH_DEFAULT;
  }
  try {
    const saved = window.localStorage.getItem(TASK_LIST_WIDTH_STORAGE_KEY);
    if (!saved) return TASK_LIST_WIDTH_DEFAULT;
    const parsed = parseInt(saved, 10);
    if (!Number.isFinite(parsed)) return TASK_LIST_WIDTH_DEFAULT;
    return Math.min(Math.max(parsed, TASK_LIST_WIDTH_MIN), TASK_LIST_WIDTH_MAX);
  } catch {
    return TASK_LIST_WIDTH_DEFAULT;
  }
}

export default function TaskEditorPage() {
  const { tasksByTaskId, saveTask, removeTask } = useTaskEditorStore();

  const tasksList = useMemo(
    () => Object.values(tasksByTaskId),
    [tasksByTaskId]
  );

  const [activeTaskId, setActiveTaskId] = useState<string | undefined>(
    undefined
  );
  const sync = useTaskEditorSyncState();
  const currentEditorState = sync.currentTask;
  const setCurrentEditorState = sync.setInitial;
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [metaModal, setMetaModal] = useState<MetaModalState | null>(null);
  const [isSavingMeta, setIsSavingMeta] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [workspaceWidth, setWorkspaceWidth] = useState<number>(
    loadInitialWorkspaceWidth
  );
  const [isResizingWorkspace, setIsResizingWorkspace] = useState(false);
  const [taskListWidth, setTaskListWidth] = useState<number>(
    loadInitialTaskListWidth
  );
  const [isResizingTaskList, setIsResizingTaskList] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        WORKSPACE_WIDTH_STORAGE_KEY,
        String(workspaceWidth)
      );
    } catch {
      // ignore
    }
  }, [workspaceWidth]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        TASK_LIST_WIDTH_STORAGE_KEY,
        String(taskListWidth)
      );
    } catch {
      // ignore
    }
  }, [taskListWidth]);

  const handleWorkspaceResizeStart = (event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = workspaceWidth;
    const dynamicMax = Math.min(
      WORKSPACE_WIDTH_MAX,
      Math.max(
        WORKSPACE_WIDTH_MIN,
        window.innerWidth - taskListWidth - CHAT_MIN_WIDTH
      )
    );
    setIsResizingWorkspace(true);

    const handleMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      const next = Math.min(
        Math.max(startWidth + delta, WORKSPACE_WIDTH_MIN),
        dynamicMax
      );
      setWorkspaceWidth(next);
    };

    const handleEnd = () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setIsResizingWorkspace(false);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleTaskListResizeStart = (event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = taskListWidth;
    const dynamicMax = Math.min(
      TASK_LIST_WIDTH_MAX,
      Math.max(
        TASK_LIST_WIDTH_MIN,
        window.innerWidth - CHAT_MIN_WIDTH - workspaceWidth
      )
    );
    setIsResizingTaskList(true);

    const handleMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const next = Math.min(
        Math.max(startWidth + delta, TASK_LIST_WIDTH_MIN),
        dynamicMax
      );
      setTaskListWidth(next);
    };

    const handleEnd = () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setIsResizingTaskList(false);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    if (!activeTaskId && tasksList.length > 0) {
      setActiveTaskId(tasksList[0].task_id);
    }
  }, [activeTaskId, tasksList]);

  useEffect(() => {
    if (!activeTaskId) {
      setCurrentEditorState(undefined);
      setHasUnsavedChanges(false);
      return;
    }
    const task = tasksByTaskId[activeTaskId];
    if (task) {
      setCurrentEditorState(cloneTaskEditorState(task));
      setHasUnsavedChanges(false);
    } else {
      setCurrentEditorState(undefined);
      setHasUnsavedChanges(false);
    }
    // tasksByTaskId 변화는 의도적으로 의존성에서 제외.
    // 사용자 편집 중 외부 atom 갱신이 currentEditorState 를 덮어쓰지 않도록.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTaskId]);

  // refresh recovery — task-editor 백엔드의 저장 버전을 sync state 에 주입한 직후, cowork
  // checkpointer 의 최신 thread 가 가진 vfs task 와 blocks hash 를 비교한다. 다르면 agent
  // 가 적용한 마지막 결과로 currentEditorState 를 교체하고 unsaved 로 표시. 이로써 사용자가
  // 저장 안 한 채 새로고침해도 agent 의 작업물이 살아남는다.
  const [recoveryToastOpen, setRecoveryToastOpen] = useState(false);
  useEffect(() => {
    if (!activeTaskId || !currentEditorState) return;
    if (currentEditorState.task_id !== activeTaskId) return;
    let cancelled = false;
    void (async () => {
      try {
        const threads = await listThreads(activeTaskId, { limit: 1 });
        if (cancelled || threads.length === 0) return;
        const sessionTask = await getSessionTask(threads[0].thread_id);
        if (cancelled || !sessionTask) return;
        if (sessionTask.task_id !== activeTaskId) return;
        const localHash = stableHashBlocks(currentEditorState.blocks);
        const sessionHash = stableHashBlocks(sessionTask.blocks);
        if (localHash === sessionHash) return;
        sync.applyUserMutation({
          ...currentEditorState,
          blocks: sessionTask.blocks,
        });
        setHasUnsavedChanges(true);
        setRecoveryToastOpen(true);
      } catch {
        // 복구 실패는 무시 — UI 흐름 유지
      }
    })();
    return () => {
      cancelled = true;
    };
    // 의도적: currentEditorState 변경마다 재실행 안 함 (사용자 편집 후 다시 복구되는 무한루프
    // 방지). activeTaskId 만 트리거.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTaskId]);

  const handleCreateTask = () => {
    setMetaModal({ mode: "create" });
  };

  const handleEditMeta = (taskId: string) => {
    const task = tasksByTaskId[taskId];
    if (!task) {
      return;
    }
    setMetaModal({
      mode: "edit",
      originalTaskId: task.task_id,
      initialValues: {
        task_id: task.task_id,
        title: task.title,
        set_number: task.set_number,
        is_key_activity: task.is_key_activity ?? false,
      },
    });
  };

  const handleMetaSubmit = async (values: TaskMetadataValues) => {
    if (!metaModal || isSavingMeta) {
      return;
    }

    let baseState: TaskEditorState;
    if (metaModal.mode === "edit") {
      // 편집 중인 local state 가 있으면 그것을 우선 사용 (변경된 블록 보존)
      const localMatch =
        currentEditorState &&
        currentEditorState.task_id === metaModal.originalTaskId
          ? currentEditorState
          : undefined;
      const fallback = tasksByTaskId[metaModal.originalTaskId];
      const source = localMatch ?? fallback;
      baseState = source
        ? cloneTaskEditorState(source as TaskEditorState)
        : createEmptyTaskEditorState();
    } else {
      baseState = createEmptyTaskEditorState();
    }

    const nextState: TaskEditorState = {
      ...baseState,
      task_id: values.task_id,
      title: values.title,
      set_number: values.set_number,
      is_key_activity: values.is_key_activity,
    };

    setIsSavingMeta(true);
    try {
      const saved = await saveTask(values.task_id, nextState);
      const persisted = saved ?? nextState;
      setActiveTaskId(persisted.task_id);
      setCurrentEditorState(cloneTaskEditorState(persisted));
      setHasUnsavedChanges(false);
      setMetaModal(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TaskEditorV2] failed to save metadata", error);
    } finally {
      setIsSavingMeta(false);
    }
  };

  const handleChangeEditorState = (next: TaskEditorState) => {
    // 사용자가 편집기에서 직접 수정한 경로. sync state 에 기록해 turn 시작 base 와의
    // diff 가 계산되도록 한다 (setInitial 은 base 까지 리셋하므로 여기선 안 됨).
    sync.applyUserMutation(next);
    setHasUnsavedChanges(true);
  };

  // Agent 가 task_patch 를 보내면 3-way merge — base(턴 시작 시점) / ours(currentTask) /
  // theirs(patch.blocks). 사용자가 같은 block 을 만지지 않았으면 그대로/auto_merge,
  // 충돌이면 banner 가 표시되고 사용자가 선택할 때까지 적용 보류 (currentTask 유지).
  // 메타데이터는 sync 내부에서 currentTask 의 메타를 보존하므로 별도 처리 불필요.
  const handleAgentTaskPatch = (patch: {
    blocks: TaskEditorState["blocks"];
  }) => {
    const outcome = sync.applyAgentPatch(patch);
    if (outcome.kind === "applied" || outcome.kind === "auto_merged") {
      setHasUnsavedChanges(true);
    }
    // conflict / no_op 은 banner 가 처리 — hasUnsavedChanges 는 적용 시점에만 켠다.
  };

  const handleConflictResolve = (choice: "accept_agent" | "keep_mine") => {
    sync.resolveConflict(choice);
    if (choice === "accept_agent") setHasUnsavedChanges(true);
  };

  const handleRevertTurn = (taskBefore: TaskEditorState) => {
    // taskBefore 는 turn 시작 시점 snapshot — 메타까지 그 시점의 것으로 돌아간다.
    sync.applyUserMutation(taskBefore);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!currentEditorState || isSavingContent) {
      return;
    }
    setIsSavingContent(true);
    try {
      const saved = await saveTask(
        currentEditorState.task_id,
        currentEditorState
      );
      if (saved) {
        setCurrentEditorState(cloneTaskEditorState(saved));
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TaskEditorV2] failed to save task", error);
    } finally {
      setIsSavingContent(false);
    }
  };

  const existingTaskIds = useMemo(
    () => Object.keys(tasksByTaskId),
    [tasksByTaskId]
  );

  return (
    <FullPageContainer>
      <Box
        sx={{
          display: "grid",
          position: "relative",
          gridTemplateColumns: `${taskListWidth}px minmax(0, 1fr) ${workspaceWidth}px`,
          height: "100%",
          width: "100%",
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: theme.vars.palette.Background.Level1,
        }}
      >
        <TaskListPanel
          tasks={tasksList}
          activeTaskId={activeTaskId}
          onSelectTask={setActiveTaskId}
          onCreateTask={handleCreateTask}
          onEditMeta={handleEditMeta}
        />

        <AgentChatPanel
          taskId={activeTaskId}
          currentTask={currentEditorState}
          onTaskPatch={handleAgentTaskPatch}
          onTurnStart={sync.markTurnStart}
          onTurnEnd={sync.markTurnEnd}
          onRevertTurn={handleRevertTurn}
        />

        <Box
          onMouseDown={handleTaskListResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="활동지 리스트 패널 크기 조정"
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${taskListWidth}px`,
            width: `${RESIZE_HANDLE_WIDTH}px`,
            cursor: "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent",
            zIndex: 2,
            "&:hover .resize-grip": {
              backgroundColor: theme.vars.palette.Content.Medium,
              opacity: 1,
            },
          }}
        >
          <Box
            className="resize-grip"
            sx={{
              width: "3px",
              height: "64px",
              borderRadius: "2px",
              backgroundColor: isResizingTaskList
                ? theme.vars.palette.Content.Medium
                : theme.vars.palette.Content.Weak,
              opacity: isResizingTaskList ? 1 : 0,
              transition: isResizingTaskList
                ? "none"
                : "opacity 0.15s, background-color 0.15s",
              pointerEvents: "none",
            }}
          />
        </Box>

        <Box
          onMouseDown={handleWorkspaceResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="우측 패널 크기 조정"
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: `${workspaceWidth}px`,
            width: `${RESIZE_HANDLE_WIDTH}px`,
            cursor: "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent",
            zIndex: 2,
            "&:hover .resize-grip": {
              backgroundColor: theme.vars.palette.Content.Medium,
              opacity: 1,
            },
          }}
        >
          <Box
            className="resize-grip"
            sx={{
              width: "3px",
              height: "64px",
              borderRadius: "2px",
              backgroundColor: isResizingWorkspace
                ? theme.vars.palette.Content.Medium
                : theme.vars.palette.Content.Weak,
              opacity: isResizingWorkspace ? 1 : 0,
              transition: isResizingWorkspace
                ? "none"
                : "opacity 0.15s, background-color 0.15s",
              pointerEvents: "none",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {sync.pendingAgentPatch && (
            <ConflictBanner
              reason={sync.pendingAgentPatch.reason}
              conflictBlockIndexes={sync.pendingAgentPatch.conflictBlockIndexes}
              onAcceptAgent={() => handleConflictResolve("accept_agent")}
              onKeepMine={() => handleConflictResolve("keep_mine")}
            />
          )}
          <RecoveryToast
            open={recoveryToastOpen}
            onDismiss={() => setRecoveryToastOpen(false)}
          />
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <TaskWorkspacePanel
              editorState={currentEditorState}
              onChangeEditorState={handleChangeEditorState}
              hasUnsavedChanges={hasUnsavedChanges}
              isSaving={isSavingContent}
              onSave={handleSave}
              onEditMeta={() => {
                if (activeTaskId) {
                  handleEditMeta(activeTaskId);
                }
              }}
            />
          </Box>
        </Box>
      </Box>

      <TaskMetadataModal
        open={metaModal !== null}
        mode={metaModal?.mode ?? "create"}
        initialValues={
          metaModal?.mode === "edit" ? metaModal.initialValues : undefined
        }
        existingTaskIds={existingTaskIds}
        isSubmitting={isSavingMeta}
        onClose={() => {
          if (!isSavingMeta) {
            setMetaModal(null);
          }
        }}
        onSubmit={handleMetaSubmit}
        onRemove={
          metaModal?.mode === "edit"
            ? async () => {
                if (isSavingMeta || metaModal.mode !== "edit") return;
                setIsSavingMeta(true);
                try {
                  await removeTask(metaModal.originalTaskId);
                  if (activeTaskId === metaModal.originalTaskId) {
                    setActiveTaskId(undefined);
                    setCurrentEditorState(undefined);
                    setHasUnsavedChanges(false);
                  }
                  setMetaModal(null);
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error("[TaskEditorV2] failed to remove task", error);
                } finally {
                  setIsSavingMeta(false);
                }
              }
            : undefined
        }
      />

      <AgentDebugPanel />
    </FullPageContainer>
  );
}
